import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import Alert from '@mui/material/Alert';
import { Box, Button, Chip, Container, Grid, Stack, Step, StepButton, Stepper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Loader } from '../../../components/Loader/Loader';
import { SectionCard } from '../../../components/Section';
import {
  TREASURY_ACCOUNT_ACTIVE_STATUS,
  TREASURY_STEP_DESCRIPTIONS,
  TREASURY_STEP_LABELS,
  TreasuryStep,
} from '../../../constants/treasury';
import { usePermissions } from '../../../hooks';
import {
  useFetchTreasuryAlpacaOnboardingQuery,
  useFetchTreasuryKybQuery,
  useFetchTreasuryKycConfirmationQuery,
  useFetchTreasurySetupQuery,
} from '../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../routes/locations';
import { TreasuryAlpacaAccount, TreasuryCompanyStatus, TreasuryKyb } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage, getTreasuryStep, isTreasuryNotSetUpError } from '../../../utils/treasury';
import { TreasurySetupStep } from './TreasurySetupStep';
import { TreasuryEmployeeStep } from './TreasuryEmployeeStep';
import { TreasuryStepSummary } from './TreasuryStepSummary';
import { TreasuryPersonalEmailStep } from './TreasuryPersonalEmailStep';
import { TreasuryApproveEmployeeStep } from './TreasuryApproveEmployeeStep';
import { TreasuryConfirmKycStep } from './TreasuryConfirmKycStep';
import { TreasuryKybStep } from './TreasuryKybStep';
import { TreasuryAdminVerificationStep } from './TreasuryAdminVerificationStep';
import { TreasuryAgreementsStep } from './TreasuryAgreementsStep';
import { TreasuryAlpacaAccountStep } from './TreasuryAlpacaAccountStep';
import { TreasuryOperations } from './Operations/TreasuryOperations';

const steps = Object.values(TREASURY_STEP_LABELS);

type CompanyTreasuryProps = {
  /** /treasury/onboarding — the steps even when the account is ACTIVE (opened from the operations page). */
  showOnboarding?: boolean;
};

/**
 * /companies/:companyId/treasury — the company's treasury. No setup yet: a "Create Treasury" button that opens the
 * onboarding steps. A setup: the steps resume where it stopped. Once the company Alpaca account is ACTIVE: the
 * operations page, with a button to the steps. The step comes from the backend, never the browser.
 */
export const CompanyTreasury = ({ showOnboarding = false }: CompanyTreasuryProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const id = Number(companyId);
  const { enqueueSnackbar } = useSnackbar();
  const canCreateTreasury = usePermissions({ items: [permissions.Company.update] });
  const [isCreating, setIsCreating] = useState(false);
  const [viewStep, setViewStep] = useState<TreasuryStep | null>(null);
  const [personalEmailSaved, setPersonalEmailSaved] = useState(false);

  // A failed setup read is "no setup" (404 → the Create button); RTK keeps the last data on a failed refetch, so the
  // error wins over it.
  const setupQuery = useFetchTreasurySetupQuery(id);
  const setup = setupQuery.isError ? null : setupQuery.data ?? null;
  const isActive = setup?.status === TreasuryCompanyStatus.Active;
  const employeeId = setup?.employeeId ?? null;

  // The owner's KYC confirmation comes after approve (the setup is ACTIVE). A failed read counts as not confirmed, so
  // the page stays on the Confirm KYC step instead of guessing past it.
  const isKycNeeded = isActive && Boolean(employeeId);
  const kycQuery = useFetchTreasuryKycConfirmationQuery(
    { companyId: id, employeeId: employeeId ?? 0 },
    { skip: !isKycNeeded },
  );
  const kycConfirmDate = isKycNeeded && !kycQuery.isError ? kycQuery.data?.confirmDate ?? null : null;

  // The KYB steps start once the setup is ACTIVE (the employee is approved). A failed read is NOT "no KYB yet": that
  // would open an empty KYB form over one that may exist. It blocks the KYB steps with a retry instead, and never
  // turns the page into "no treasury yet".
  const kybQuery = useFetchTreasuryKybQuery(id, { skip: !isActive });
  const kyb: TreasuryKyb | null = isActive && !kybQuery.isError ? kybQuery.data ?? null : null;
  const kybLoadError =
    isActive && kybQuery.isError ? getTreasuryErrorMessage(kybQuery.error, 'Failed to load the company KYB') : null;

  // The account exists only after the agreements; a failed read keeps the steps (money never moves on a guess). The
  // Alpaca step polls this same entry, so the page moves to the operations as soon as the account reads ACTIVE.
  const isAccountNeeded = Boolean(kyb?.agreements || setup?.agreements);
  const accountQuery = useFetchTreasuryAlpacaOnboardingQuery(id, { skip: !isAccountNeeded });
  const account: TreasuryAlpacaAccount | null =
    isAccountNeeded && !accountQuery.isError ? accountQuery.data?.account ?? null : null;

  // Every read of the setup, KYC or KYB reloads the steps (they remount on the new answers), as the page always did.
  // The account only on its first read: the Alpaca step polls it, and a spinner per poll would unmount that step.
  const isLoading =
    setupQuery.isFetching ||
    (isKycNeeded && kycQuery.isFetching) ||
    (isActive && kybQuery.isFetching) ||
    (isAccountNeeded && accountQuery.isLoading);

  useEffect(() => {
    if (setupQuery.isError && !isTreasuryNotSetUpError(setupQuery.error)) {
      enqueueSnackbar(getTreasuryErrorMessage(setupQuery.error, 'Failed to load the treasury'), { variant: 'error' });
    }
  }, [setupQuery.isError, setupQuery.error, enqueueSnackbar]);

  const { refetch: refetchSetup } = setupQuery;
  const { refetch: refetchKyb } = kybQuery;

  // A step finished: back to the current step, on fresh data. A step's write already refetched what it changed (RTK
  // skips this second read while that one runs); a step that wrote nothing (the onboarding finished) needs it.
  const loadSetup = useCallback(() => {
    setViewStep(null);
    refetchSetup();
  }, [refetchSetup]);

  // The personal email step leaves no mark in the backend, so saving it moves the page to Approve here.
  const backendStep = getTreasuryStep(setup, kyb, Boolean(kycConfirmDate));
  const activeStep =
    backendStep === TreasuryStep.PersonalEmail && personalEmailSaved ? TreasuryStep.ApproveEmployee : backendStep;
  const showSteps = Boolean(setup) || isCreating;
  // A finished step opened with Back (read only), or null for the current step.
  const shownStep = viewStep ?? activeStep;
  const isViewingPastStep = viewStep !== null && viewStep < activeStep;

  const renderStep = () => {
    // The KYB couldn't be read: every step from the KYB on depends on it, so none is shown until it loads.
    if (kybLoadError && shownStep >= TreasuryStep.CompanyKyb) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetchKyb}>
              Retry
            </Button>
          }
        >
          {kybLoadError}
        </Alert>
      );
    }

    // The KYB can be saved again until it goes to Alpaca, so Back opens it as the (editable) form.
    if (shownStep === TreasuryStep.CompanyKyb && setup) {
      return (
        <TreasuryKybStep
          companyId={Number(companyId)}
          kyb={kyb}
          ownerUserId={setup.employee?.external_user_id ?? null}
          onDone={loadSetup}
        />
      );
    }

    // "Company verified" can be confirmed again (it replaces the saved one), so Back opens it as the form too.
    if (shownStep === TreasuryStep.AdminVerification && setup) {
      return (
        <TreasuryAdminVerificationStep
          key={kyb?.verification?.verifiedAt ?? 'not-verified'}
          companyId={Number(companyId)}
          verification={kyb?.verification ?? null}
          onDone={loadSetup}
        />
      );
    }

    if (isViewingPastStep && setup) {
      return <TreasuryStepSummary step={shownStep} setup={setup} kyb={kyb} kycConfirmDate={kycConfirmDate} />;
    }

    if (activeStep === TreasuryStep.GroupAndDepartment) {
      return <TreasurySetupStep companyId={Number(companyId)} onDone={loadSetup} />;
    }

    if (activeStep === TreasuryStep.TreasuryEmployee && setup) {
      return <TreasuryEmployeeStep setup={setup} onDone={loadSetup} />;
    }

    if (activeStep === TreasuryStep.PersonalEmail && setup) {
      return (
        <TreasuryPersonalEmailStep
          setup={setup}
          onDone={() => {
            setPersonalEmailSaved(true);
            setViewStep(null);
          }}
        />
      );
    }

    if (activeStep === TreasuryStep.ApproveEmployee && setup) {
      return <TreasuryApproveEmployeeStep setup={setup} onDone={loadSetup} />;
    }

    if (activeStep === TreasuryStep.ConfirmKyc && setup) {
      return <TreasuryConfirmKycStep setup={setup} onDone={loadSetup} />;
    }

    if (activeStep === TreasuryStep.AlpacaAgreements && setup) {
      return <TreasuryAgreementsStep setup={setup} onDone={loadSetup} />;
    }

    if (activeStep === TreasuryStep.AlpacaAccount && setup) {
      return (
        <TreasuryAlpacaAccountStep
          companyId={Number(companyId)}
          ownerUserId={setup.employee?.external_user_id ?? null}
          onDone={loadSetup}
        />
      );
    }

    return null;
  };

  // Open a step from the list: a finished one to review (or edit, for the KYB / verification), the current one to work.
  const openStep = (step: TreasuryStep) => setViewStep(step === activeStep ? null : step);
  const statusChip = setup ? (
    <Chip
      size="small"
      label={setup.status === TreasuryCompanyStatus.Active ? 'Active' : 'Pending'}
      color={setup.status === TreasuryCompanyStatus.Active ? 'success' : 'warning'}
    />
  ) : (
    <Chip size="small" label="Not set up" variant="outlined" />
  );

  const isAccountActive = account?.status === TREASURY_ACCOUNT_ACTIVE_STATUS;
  const showOperations = Boolean(setup && account && isAccountActive && !showOnboarding);

  return (
    <Container component="main" maxWidth="xl">
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pb: 3 }}>
        <Typography variant="h4">Treasury</Typography>
        {!isLoading && statusChip}
        <Box sx={{ flexGrow: 1 }} />
        {!isLoading && isAccountActive && (
          <Button
            variant="outlined"
            component={RouterLink}
            to={showOperations ? locations.companyTreasuryOnboarding(companyId) : locations.companyTreasury(companyId)}
            startIcon={showOperations ? <ChecklistIcon /> : <AccountBalanceIcon />}
          >
            {showOperations ? 'Onboarding' : 'Open Treasury'}
          </Button>
        )}
      </Stack>

      <Loader isLoading={isLoading}>
        {showOperations && setup && account && <TreasuryOperations setup={setup} account={account} />}
        {!showOperations && showSteps && (
          <Grid container spacing={3} alignItems="flex-start">
            {/* The steps — finished ones open for review, later ones wait. */}
            <Grid item xs={12} md={3}>
              <SectionCard title="Onboarding" description={`Step ${activeStep + 1} of ${steps.length}`}>
                <Stepper orientation="vertical" nonLinear activeStep={shownStep}>
                  {steps.map((label, index) => {
                    const step = index as TreasuryStep;
                    const isDone = step < activeStep;
                    const isReachable = step <= activeStep;

                    return (
                      <Step key={label} completed={isDone}>
                        <StepButton
                          onClick={() => openStep(step)}
                          disabled={!isReachable}
                          optional={
                            step === activeStep ? (
                              <Typography variant="caption" color="primary">
                                Current step
                              </Typography>
                            ) : undefined
                          }
                        >
                          {label}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
              </SectionCard>
            </Grid>

            {/* The open step. */}
            <Grid item xs={12} md={9}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">{TREASURY_STEP_LABELS[shownStep]}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {TREASURY_STEP_DESCRIPTIONS[shownStep]}
                  </Typography>
                </Box>
                {isViewingPastStep &&
                  shownStep !== TreasuryStep.CompanyKyb &&
                  shownStep !== TreasuryStep.AdminVerification && (
                    <Alert
                      severity="info"
                      action={
                        <Button color="inherit" size="small" onClick={() => setViewStep(null)}>
                          Back to current step
                        </Button>
                      }
                    >
                      You are reviewing a finished step.
                    </Alert>
                  )}
                {renderStep()}
              </Stack>
            </Grid>
          </Grid>
        )}
        {!showOperations && !showSteps && (
          <SectionCard title="This company has no treasury yet">
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                Treasury lets the company invest its own money. Creating it walks through nine steps: the group and
                department, the treasury employee (the company owner) and their KYC, the company KYB, the agreements and
                the Alpaca account.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsCreating(true)}
                disabled={!canCreateTreasury}
              >
                Create Treasury
              </Button>
            </Stack>
          </SectionCard>
        )}
      </Loader>
    </Container>
  );
};
